// ProductDetailspage.js - Bootstrap Enhanced Responsive Version with Table Plans
import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faChevronLeft, 
  faChevronRight, 
  faPlayCircle, 
  faFileAlt,
  faCheck,
  faTimes
} from '@fortawesome/free-solid-svg-icons'
import { faGithub } from '@fortawesome/free-brands-svg-icons'
import Topnavbar from '../Header-pages/Topnavbar'
import Navbar from '../Header-pages/Navbar'
import Footer from '../Header-pages/Footer'
import { API_URL } from '../utils/Function'
import '../Css/ProductDetailspage.css'
import { Helmet } from "react-helmet-async";

export default function ProductDetailspage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [activeTab, setActiveTab] = useState('overview')

  const fetchProductDetails = useCallback(async () => {
    try {
      if (location.state?.product) {
        setProduct(location.state.product)
        setLoading(false)
        return
      }

      const response = await fetch(`${API_URL}/product.php/get/${slug}`)
      const data = await response.json()
      
      
      if (data.success) {
        setProduct(data.product)
      } else {
        setError(data.error || 'Product not found')
      }
    } catch (err) {
      setError('Network error: ' + err.message)
    } finally {
      setLoading(false)
    }
  }, [slug, location.state])

  useEffect(() => {
    fetchProductDetails()
  }, [fetchProductDetails])

  // Auto slide functionality
  useEffect(() => {
    if (!product?.gallery_images_urls?.length) return

    const interval = setInterval(() => {
      setCurrentImageIndex(prev => 
        prev === product.gallery_images_urls.length - 1 ? 0 : prev + 1
      )
    }, 5000)

    return () => clearInterval(interval)
  }, [product?.gallery_images_urls])

  const nextImage = () => {
    if (product?.gallery_images_urls) {
      setCurrentImageIndex(prev => 
        prev === product.gallery_images_urls.length - 1 ? 0 : prev + 1
      )
    }
  }

  const prevImage = () => {
    if (product?.gallery_images_urls) {
      setCurrentImageIndex(prev => 
        prev === 0 ? product.gallery_images_urls.length - 1 : prev - 1
      )
    }
  }

  const goToImage = (index) => {
    setCurrentImageIndex(index)
  }

const renderFeatureIcon = (value) => {
  if (!value || value === "✗") {
    return (
      <div className="icon-center">
        <div className="icon-wrapper cross">
          <FontAwesomeIcon icon={faTimes} />
        </div>
      </div>
    );
  }

  if (value === "✓") {
    return (
      <div className="icon-center">
        <div className="icon-wrapper check">
          <FontAwesomeIcon icon={faCheck} />
        </div>
      </div>
    );
  }

  // text values → left aligned
  return <span className="text-left-value">{value}</span>;
};


  if (loading) {
    return (
      <>
        <Topnavbar />
        <Navbar />
        <div className="productdetails-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading product details...</p>
        </div>
        <Footer />
      </>
    )
  }

  if (error || !product) {
    return (
      <>
        <Topnavbar />
        <Navbar />
        <div className="productdetails-error">
          <p>{error || 'Product not found'}</p>
          <button onClick={() => navigate('/Productsview')} className="productdetails-back-btn">
            Back to Products
          </button>
        </div>
        <Footer />
      </>
    )
  }
  const images = product.gallery_images_urls || []
  const hasGallery = images.length > 0
  const mainImage = hasGallery ? images[currentImageIndex] : product.featured_image_url

  // Safely parse arrays with fallbacks
  const technologiesArray = product.technologies_array || 
                           (product.technologies ? JSON.parse(product.technologies) : []) || 
                           []

  const useCasesArray = product.use_cases_array || 
                       (product.use_cases ? JSON.parse(product.use_cases) : []) || 
                       []

  const featuresArray = product.features_array || 
                       (product.features ? JSON.parse(product.features) : []) || 
                       []

  // Get plan overview array
  const planOverviewArray = product.plan_overview_array || []

  // Check which plans are available
  const hasBasicPlan = product.plan_basic_desc && product.plan_basic_desc.trim() !== ''
  const hasStandardPlan = product.plan_standard_desc && product.plan_standard_desc.trim() !== ''
  const hasPremiumPlan = product.plan_premium_desc && product.plan_premium_desc.trim() !== ''

  

  return (
    <>
    <Helmet>
        <title>
          {product?.meta_title 
            ? product.meta_title
            : `${product?.name} | SAFPRO Technology Solutions`}
        </title>

        <meta 
          name="description" 
          content={product?.meta_description || product?.tagline || product?.name} 
        />
      </Helmet>

      <Topnavbar />
      <Navbar />
      
      <div className="productdetails-page">
        {/* Hero Section with Background Image */}
        <div className="productdetails-hero" style={{ backgroundImage: `url("/images/Productview.jpg")` }}>
          <div className="productdetails-hero-overlay">
            <div className="container">
              {/* Breadcrumbs */}
              <nav aria-label="breadcrumb">
                <div className="productdetails-breadcrumbs">
                  <a onClick={()=>navigate('/')} style={{color:"#fff", cursor:'pointer'}}>Home</a>
                  <span>/</span>
                  <a onClick={()=>navigate('/Productsview')} style={{color:"#fff",cursor:'pointer'}}>Products</a>
                  <span>/</span>
                  <span>{product.name}</span>
                </div>
              </nav>

              {/* Product Title */}
              <h1 className="productdetails-hero-title">{product.name}</h1>
              <p className="productdetails-hero-subtitle">{product.tagline}</p>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="productdetails-content">
          <div className="container">
            <div className="row g-4">
              {/* Left Column */}
              <div className="col-lg-7 col-xl-8">
                <div className="productdetails-left">
                  {/* Category Badge */}
                  <div className="productdetails-category-badge">
                    {product.category || 'Web Development'}
                  </div>

                  {/* Title and Tagline with spacing */}
                  <div className="productdetails-info-header">
                    <h2 className="productdetails-product-name">{product.name}</h2>
                    <p className="productdetails-product-tagline">{product.tagline}</p>
                  </div>

                  {/* Action Buttons */}
                  <div className="productdetails-actions-top">
                    <button className="productdetails-btn-trial">Start Free Trial</button>
                    {product.demo_url && (
                      <a 
                        href={product.demo_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="productdetails-btn-demo"
                      >
                        Live Demo
                      </a>
                    )}
                  </div>

                  <div className="productdetails-tabs mb-4">
                    <h4 className="fw-bold text-dark mb-2">Overview</h4>
                    <div style={{ height: "2px", width: "100%" }}></div>
                  </div>

                  {/* Tab Content */}
                  <div className="productdetails-tab-content">
                    {activeTab === 'overview' && (
                      <div className="productdetails-overview">
                        <h3>Product Description</h3>
                        <p>{product.description}</p>

                        {/* Technologies Section */}
                        {technologiesArray.length > 0 && (
                          <>
                            <h3>Key Technologies</h3>
                            <div className="productdetails-tech-list">
                              {technologiesArray.map((tech, index) => (
                                <span key={index} className="productdetails-tech-badge">
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </>
                        )}

                        {/* Use Cases Section */}
                        {useCasesArray.length > 0 && (
                          <>
                            <h3>Use Cases</h3>
                            <ul className="productdetails-use-cases-list">
                              {useCasesArray.map((useCase, index) => (
                                <li key={index}>{useCase}</li>
                              ))}
                            </ul>
                          </>
                        )}

                        {/* Features Section */}
                        {featuresArray.length > 0 && (
                          <>
                            <h3>Key Features</h3>
                            <ul className="productdetails-features-list">
                              {featuresArray.map((feature, index) => (
                                <li key={index}>{feature}</li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Gallery */}
              <div className="col-lg-5 col-xl-4">
                <div className="productdetails-right">
                  {/* Gallery Card with Shadow */}
                  <div className="productdetails-gallery-card">
                    <div className="productdetails-main-image">
                      {mainImage ? (
                        <img 
                          src={`${API_URL}/${mainImage}`}
                          alt={product.name}
                          className="img-fluid"
                          onError={(e) => {
                            e.target.src = '/images/placeholder-product.png'
                          }}
                        />
                      ) : (
                        <div className="productdetails-image-placeholder">
                          <span>No Image Available</span>
                        </div>
                      )}
                      
                      {/* Navigation Arrows */}
                      {hasGallery && images.length > 1 && (
                        <>
                          <button 
                            className="productdetails-arrow productdetails-arrow-prev"
                            onClick={prevImage}
                            aria-label="Previous image"
                          >
                            <FontAwesomeIcon icon={faChevronLeft} />
                          </button>
                          <button 
                            className="productdetails-arrow productdetails-arrow-next"
                            onClick={nextImage}
                            aria-label="Next image"
                          >
                            <FontAwesomeIcon icon={faChevronRight} />
                          </button>
                        </>
                      )}
                    </div>

                    {/* Thumbnail Gallery */}
                    {hasGallery && images.length > 1 && (
                      <div className="productdetails-thumbnails">
                        {images.map((image, index) => (
                          <button
                            key={index}
                            className={`productdetails-thumbnail ${
                              index === currentImageIndex ? 'active' : ''
                            }`}
                            onClick={() => goToImage(index)}
                            aria-label={`View image ${index + 1}`}
                          >
                            <img 
                              src={`${API_URL}/${image}`}
                              alt={`View ${index + 1}`}
                              className="img-fluid"
                              onError={(e) => {
                                e.target.src = '/images/placeholder-product.png'
                              }}
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Pricing Card - No Shadow, Now Sticky */}
                  <div className="productdetails-pricing-card">
                    <div className="productdetails-quick-links">
                      <h4>Quick Links</h4>
                      {product.demo_url && (
                        <a href={product.demo_url} target="_blank" rel="noopener noreferrer">
                          <FontAwesomeIcon icon={faPlayCircle} /> Live Demo
                        </a>
                      )}
                      {product.documentation_url && (
                        <a href={product.documentation_url} target="_blank" rel="noopener noreferrer">
                          <FontAwesomeIcon icon={faFileAlt} /> Download Documentation
                        </a>
                      )}
                      {product.github_url && (
                        <a href={product.github_url} target="_blank" rel="noopener noreferrer">
                          <FontAwesomeIcon icon={faGithub} /> View on GitHub
                        </a>
                      )}
                    </div>

                    {product.target_audience && (
                      <div className="productdetails-perfect-for">
                        <h4>Perfect For</h4>
                        <p>{product.target_audience}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Plans Section - Table Format */}
        {(hasBasicPlan || hasStandardPlan || hasPremiumPlan) && (
          <div className="productdetails-plans-section">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <h2 className="productdetails-plans-title">Choose Your Plan</h2>
                  <p className="productdetails-plans-subtitle">Select the perfect plan for your needs</p>
                </div>
              </div>

              {/* Plan Descriptions */}
              <div className="row g-4 mb-4">
                {hasBasicPlan && (
                  <div className="col-lg-4">
                    <div className="plan-description-card">
                      <h4 className="plan-desc-title">Basic Plan</h4>
                      <p className="plan-desc-text">{product.plan_basic_desc}</p>
                    </div>
                  </div>
                )}
                {hasStandardPlan && (
                  <div className="col-lg-4">
                    <div className="plan-description-card plan-desc-featured">
                      <h4 className="plan-desc-title">Standard Plan</h4>
                      <p className="plan-desc-text">{product.plan_standard_desc}</p>
                    </div>
                  </div>
                )}
                {hasPremiumPlan && (
                  <div className="col-lg-4">
                    <div className="plan-description-card">
                      <h4 className="plan-desc-title">Premium Plan</h4>
                      <p className="plan-desc-text">{product.plan_premium_desc}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Plan Comparison Table */}
              {planOverviewArray && planOverviewArray.length > 0 && (
                <div className="row">
                  <div className="col-12">
                    <div className="plan-comparison-table-wrapper">
                      <table className="plan-comparison-table table table-bordered">
                        <thead>
                          <tr>
                            <th className="overview-heading-col">Overview</th>
                            {hasBasicPlan && <th className="plan-col plan-basic">Basic</th>}
                            {hasStandardPlan && <th className="plan-col plan-standard">Standard</th>}
                            {hasPremiumPlan && <th className="plan-col plan-premium">Premium</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {planOverviewArray.map((row, index) => (
                            <tr key={index} className={index < planOverviewArray.length - 1 ? "border-bottom-row" : ""}>
                              <td className="overview-heading-cell">{row.heading}</td>
                              {hasBasicPlan && (
                                <td className="plan-feature-cell">
                                  {renderFeatureIcon(row.basic)}
                                </td>
                              )}
                              {hasStandardPlan && (
                                <td className="plan-feature-cell">
                                  {renderFeatureIcon(row.standard)}
                                </td>
                              )}
                              {hasPremiumPlan && (
                                <td className="plan-feature-cell">
                                  {renderFeatureIcon(row.premium)}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td></td>
                            {hasBasicPlan && (
                              <td>
                                <button 
                                  className="plan-action-btn btn-basic"
                                  onClick={() => navigate('/contactus', {
                                    state: {
                                      planInfo: {
                                        productName: product.name,
                                        planName: 'Basic',
                                        planType: 'basic'
                                      }
                                    }
                                  })}
                                >
                                  Contact Us
                                </button>
                              </td>
                            )}
                            {hasStandardPlan && (
                              <td>
                                <button 
                                  className="plan-action-btn btn-standard"
                                  onClick={() => navigate('/contactus', {
                                    state: {
                                      planInfo: {
                                        productName: product.name,
                                        planName: 'Standard',
                                        planType: 'standard'
                                      }
                                    }
                                  })}
                                >
                                  Contact Us
                                </button>
                              </td>
                            )}
                            {hasPremiumPlan && (
                              <td>
                                <button 
                                  className="plan-action-btn btn-premium"
                                  onClick={() => navigate('/contactus', {
                                    state: {
                                      planInfo: {
                                        productName: product.name,
                                        planName: 'Premium',
                                        planType: 'premium'
                                      }
                                    }
                                  })}
                                >
                                  Contact Us
                                </button>
                              </td>
                            )}
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </>
  )
}